/* eslint-disable max-statements */
import React from 'react';
import { AppError } from 'src/redux/types';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { invalidatePurchaseCosmetic, purchaseCosmetic } from 'src/redux/actions';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import Spinner from 'src/Components/Spinner';
import ErrorMessage from './ErrorMessage';
import styles from './styles.module.scss';
import icClose from 'assets/svg/ic_close.svg';
import icThetanCoin from 'assets/img/thetan_coin.png';
import { formatCurrency } from '@wolffun/system-currency';
import Modal from 'src/Components/Modal';
import { BN } from 'bn.js';
import { formatErrorMessage } from './utils';
import { parseCosmeticType } from 'src/Pages/MarketplaceV2/Components/MarketCosmetic/utils';
import Button from 'src/Pages/CosmeticDetail/Components/Button';
import { getPreferredWalletProviderName } from 'src/utils/session';
import useCheckAllowance from 'src/hooks/useCheckAllowance';
import { THETA_MARKETPLACE_WALLET_ADDRESS } from 'src/constants';

const TransactionConfirmModal = ({
  cosmetic,
  onClose,
  onCheckoutCompleted
}) => {
  const dispatch = useTypedDispatch();
  const price = useTypedSelector(state => {
    var _state$cosmeticDetail;

    return (_state$cosmeticDetail = state.cosmeticDetail.data) === null || _state$cosmeticDetail === void 0 ? void 0 : _state$cosmeticDetail.saleInfo.price;
  });
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const cosmeticLoading = useTypedSelector(state => state.purchaseCosmetic.loading);
  const [showWaiting, setShowWaiting] = React.useState(false);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const errorMessage = React.useMemo(() => {
    return formatErrorMessage(error);
  }, [error]);
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: THETA_MARKETPLACE_WALLET_ADDRESS,
    thresholdPrice: price
  });
  React.useEffect(() => {
    const thcBalanceBN = thcBalance ? new BN(thcBalance.value) : undefined;
    const priceBN = price ? new BN(price.value) : undefined;

    if (priceBN && thcBalanceBN && thcBalanceBN.lt(priceBN)) {
      setError(AppError.INSUFFICIENT_BALANCE);
    }
  }, [thcBalance, price]);
  React.useEffect(() => {
    if (cosmeticLoading) {
      const timeout = setTimeout(() => setShowWaiting(true), 10 * 1000);
      return () => clearTimeout(timeout);
    } else {
      setShowWaiting(false);
      return undefined;
    }
  }, [cosmeticLoading]);
  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    dispatch(updateWalletBalance());
    dispatch(invalidatePurchaseCosmetic());
  };

  const onButtonPressed = async () => {
    if (price) {
      const {
        data,
        error: sellCosmeticError
      } = await dispatch(purchaseCosmetic({
        blockchainId: cosmetic.blockchainId,
        systemPrice: price
      }));

      if (sellCosmeticError !== undefined && sellCosmeticError !== AppError.NO_ERROR) {
        setError(sellCosmeticError);
        return;
      }

      if (data !== null && data !== void 0 && data.id && thcBalance) {
        onCheckoutCompleted(cosmetic, thcBalance);
      }
    }
  };

  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Buy Cosmetic"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    disabled: cosmeticLoading,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subtitle
  }, /*#__PURE__*/React.createElement("span", null, `You are about to buy #${cosmetic.tokenId} - ${parseCosmeticType(cosmetic.cosmeticType)} ${cosmetic.cosmeticName}`)), /*#__PURE__*/React.createElement("div", {
    className: styles.detailSection
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("span", null, "Price")), /*#__PURE__*/React.createElement("div", {
    className: styles.price
  }, /*#__PURE__*/React.createElement("span", null, `${price && formatCurrency(price, {
    withCurrency: true
  })} `, /*#__PURE__*/React.createElement("img", {
    className: styles.currencyIcon,
    src: icThetanCoin
  }))))), /*#__PURE__*/React.createElement("div", {
    className: styles.priceBreakdownRow
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.label
  }, /*#__PURE__*/React.createElement("span", null, "Your balance")), /*#__PURE__*/React.createElement("div", {
    className: styles.price
  }, /*#__PURE__*/React.createElement("span", null, thcBalance && formatCurrency(thcBalance, {
    withCurrency: true
  })))), errorMessage ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    title: errorMessage.title,
    description: errorMessage.description
  }) : null, showWaiting ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    warning: true,
    title: "It may take a while...",
    description: `We are still waiting for transaction to be processed. Please be patient. You might need to check your ${getPreferredWalletProviderName() || ''} wallet.`
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, cosmeticLoading || isProcessing ? /*#__PURE__*/React.createElement(Button, {
    disabled: true,
    loading: true,
    title: "Processing"
  }, /*#__PURE__*/React.createElement(Spinner, {
    className: styles.loading,
    secondary: true,
    size: 24
  })) : isEnoughAllowance ? /*#__PURE__*/React.createElement(Button, {
    onClick: onButtonPressed,
    disabled: error !== AppError.NO_ERROR,
    title: "Buy"
  }) : /*#__PURE__*/React.createElement(Button, {
    onClick: async () => {
      const {
        error
      } = await approveUseBalance();

      if (error) {
        setError(error);
      }
    },
    disabled: isProcessing,
    title: "Approve"
  })))));
};

export default TransactionConfirmModal;